import { Directive, ElementRef, forwardRef, Host } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DatePicker } from '@vaadin/date-picker';
import { fromEvent, merge, Observable, Subscription } from 'rxjs';
import { UnsubscribeOnDestroy } from '@app/util/lifecycle-hooks/lifecycle-hooks';
import { map, startWith } from 'rxjs/operators';
import { formatISO } from 'date-fns';

@Directive({
	// eslint-disable-next-line @angular-eslint/directive-selector
	selector: 'vaadin-date-picker[ngModel],vaadin-date-picker[formControlName]',
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			multi: true,
			useExisting: forwardRef(()=> DatePickerValueAccessorDirective),
		},
	],
})
// eslint-disable-next-line rxjs-angular/prefer-composition
export class DatePickerValueAccessorDirective implements ControlValueAccessor {
	@UnsubscribeOnDestroy
	private readonly subscription = new Subscription();
	
	public readonly value$: Observable<Date | null>;
	private _onChanged?: (value: (Date | null)) => void;
	private _onTouched?: () => void;
	
	constructor(@Host() private readonly host: ElementRef<DatePicker>) {
		console.assert(this.host.nativeElement instanceof DatePicker, 'host is of type DatePicker');
		
		this.value$ = fromEvent(host.nativeElement, 'change')
			.pipe(
				map(() => host.nativeElement.value),
				startWith<string, null>(null),
				map(value => value !== null ? new Date(value) : null),
			);
		
		this.subscription.add(
			this.value$.subscribe(value => this._onChanged?.(value)),
		);
		
		const touched$ = merge([
			fromEvent(host.nativeElement, 'blur'),
		]);
		
		this.subscription.add(
			touched$.subscribe(() => this._onTouched?.()),
		);
	}
	
	public registerOnChange(callback: (value: Date | null) => void): void {
		this._onChanged = callback;
	}
	
	public registerOnTouched(callback: () => void): void {
		this._onTouched = callback;
	}
	
	public writeValue(value: Date | string | null): void {
		if (value instanceof Date) {
			value = formatISO(value, {representation:'date'});
		}
		
		if (value === null) {
			value = '';
		}
		
		this.host.nativeElement.value = value;
	}
}